import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { extractYoutubeId } from "@/components/academy/VideoCard";

interface ButtonData {
  text: string;
  url: string;
  variant: "primary" | "outline";
}

interface HeroSectionProps {
  title: string;
  subtitle: string;
  content: string;
  youtubeUrl?: string;
  buttons?: ButtonData[];
  backgroundColor?: string;
  onButtonClick?: (url: string, text: string) => void;
}

export function HeroSection({ title, subtitle, content, youtubeUrl, buttons = [], backgroundColor, onButtonClick }: HeroSectionProps) {
  const videoId = youtubeUrl ? extractYoutubeId(youtubeUrl) : null;
  const hasMedia = !!videoId;

  return (
    <section
      className="py-16 sm:py-24 px-4"
      style={{ backgroundColor: backgroundColor || undefined }}
    >

      <div className={`relative max-w-6xl mx-auto ${hasMedia ? "grid md:grid-cols-2 gap-12 items-center" : "text-center"}`}>
        <div className={`space-y-6 ${hasMedia ? "text-left" : "max-w-3xl mx-auto"}`}>
          {subtitle && (
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-primary bg-primary/10 px-4 py-1.5 rounded-full">
              {subtitle}
            </span>
          )}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground leading-[1.15] tracking-tight">
            {title}
          </h1>
          {content && (
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              {content}
            </p>
          )}

          {buttons.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              {buttons.map((btn, i) => (
                <Button
                  key={i}
                  variant={btn.variant === "outline" ? "outline" : "default"}
                  size="lg"
                  className={`text-base px-8 py-6 font-semibold transition-all duration-300 ${
                    btn.variant !== "outline"
                      ? "shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5"
                      : "hover:-translate-y-0.5"
                  }`}
                  asChild
                >
                <a href={btn.url} target="_blank" rel="noopener noreferrer" onClick={() => onButtonClick?.(btn.url, btn.text)}>
                    {btn.text}
                  </a>
                </Button>
              ))}
            </div>
          )}
        </div>

        {videoId && (
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl blur-sm" />
            <div className="relative rounded-xl overflow-hidden shadow-2xl ring-1 ring-border/50">
              <AspectRatio ratio={16 / 9}>
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                  title={title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </AspectRatio>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
